import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Button — upgraded in Phase 12 for WCAG 2.2 + Neurodesign.
 *
 * Backward-compatible: every existing variant and size keeps its
 * previous class surface. New features are opt-in via additional props
 * and aliases:
 *   - variant 'primary' mirrors 'default'; 'danger' mirrors 'destructive';
 *     'success' is new.
 *   - size 'md' mirrors 'default'; 'xs' and 'xl' extend the scale.
 *   - `minTouchTarget` ensures the control is ≥ 44×44 (WCAG 2.5.8
 *     Apple/Material minimum) regardless of the visual size.
 *   - `loading` swaps content for a spinner and sets aria-busy.
 *   - `loadingText` is an sr-only announcement during loading.
 *   - `ariaLabel` / `ariaDescribedBy` forward to aria-label /
 *     aria-describedby — convenient when consumers pass non-HTML props.
 *   - `celebrateOnClick` adds a one-shot pulse on successful click.
 *   - `pressEffect` = 'none' | 'scale' | 'depress' — press feedback.
 *
 * Focus ring bumped from ring-1 → ring-2 to satisfy WCAG 2.4.13.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        primary:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        danger:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        success:
          "bg-success text-white shadow-sm hover:bg-success/90",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        xs: "h-8 rounded-md px-2 text-xs",
        sm: "h-8 rounded-md px-3 text-xs",
        md: "h-9 px-4 py-2",
        lg: "h-10 rounded-md px-8",
        xl: "h-12 rounded-md px-10 text-base",
        icon: "h-9 w-9",
      },
      minTouchTarget: {
        true: "min-h-touch-lg min-w-touch-lg",
        false: "min-h-touch min-w-touch",
      },
      pressEffect: {
        none: "",
        scale: "active:scale-[0.98] transition-transform duration-100",
        depress: "active:translate-y-px transition-transform duration-100",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      minTouchTarget: false,
      pressEffect: "none",
    },
  }
)

const Spinner = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-4 w-4 animate-spin"
    fill="none"
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
    <path
      d="M4 12a8 8 0 018-8"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      minTouchTarget,
      pressEffect,
      loading = false,
      loadingText,
      celebrateOnClick = false,
      ariaLabel,
      ariaDescribedBy,
      onClick,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const [celebrating, setCelebrating] = React.useState(false);

    const handleClick = React.useCallback(
      (event) => {
        if (loading || disabled) {
          event.preventDefault();
          return;
        }
        if (celebrateOnClick) {
          setCelebrating(true);
          window.setTimeout(() => setCelebrating(false), 320);
        }
        if (onClick) onClick(event);
      },
      [celebrateOnClick, disabled, loading, onClick]
    );

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, minTouchTarget, pressEffect, className }),
          celebrating && "animate-pulse-glow"
        )}
        ref={ref}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-busy={loading ? true : undefined}
        aria-disabled={loading || disabled ? true : undefined}
        disabled={disabled || loading || undefined}
        onClick={handleClick}
        {...props}
      >
        {asChild ? (
          // Radix Slot requires exactly one React element child; the
          // conditional Spinner / loadingText siblings (even when falsy)
          // confuse Children.count and trigger Children.only.
          children
        ) : (
          <>
            {loading && <Spinner />}
            {children}
            {loading && loadingText && (
              <span className="ds-visually-hidden">{loadingText}</span>
            )}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button"

export { Button, buttonVariants }
